import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { 
  HiOutlineHome, 
  HiOutlineTrash, 
  HiOutlineCheckCircle,
  HiOutlineExclamationCircle,
  HiOutlineBellAlert,
  HiOutlineInformationCircle
} from "react-icons/hi2";
import { toast } from "react-toastify";
import { notificationService } from "../services";
import "./NotificationsPage.css";

const TAB_OPTIONS = [
  { value: "all", label: "All Alerts", icon: HiOutlineBellAlert },
  { value: "unread", label: "Unread", icon: HiOutlineInformationCircle },
  { value: "action_required", label: "Actions Needed", icon: HiOutlineExclamationCircle },
  { value: "success", label: "Success", icon: HiOutlineCheckCircle },
];

const NotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const navigate = useNavigate();

  const fetchLogs = useCallback(async (pageNum, isAppend = false) => {
    try {
      if (isAppend) setLoadingMore(true);
      else setLoading(true);

      const params = {
        page: pageNum,
        pageSize: 20,
        isRead: activeTab === "unread" ? false : null,
        type: (activeTab !== "all" && activeTab !== "unread") ? activeTab : null
      };

      const data = await notificationService.getNotifications(params);
      const items = data?.items || [];
      
      if (isAppend) {
        setNotifications(prev => [...prev, ...items]);
      } else {
        setNotifications(items);
      }
      
      setHasMore(items.length === 20);
    } catch (err) {
      console.error("Failed to load notifications:", err);
      toast.error("Could not load notifications.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [activeTab]);

  useEffect(() => {
    setPage(1);
    fetchLogs(1, false);
  }, [fetchLogs]);

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchLogs(nextPage, true);
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      toast.success("Everything marked as read.");
    } catch (err) {
      toast.error("Failed to mark all as read.");
    }
  };

  const handleMarkRead = async (id) => {
    try {
      await notificationService.markAsRead(id);
      setNotifications(prev => prev.map(n => 
        n.notificationId === id ? { ...n, isRead: true } : n
      ));
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    try {
      await notificationService.deleteNotification(id);
      setNotifications(prev => prev.filter(n => n.notificationId !== id));
      toast.info("Notification removed.");
    } catch (err) {
      toast.error("Delete failed.");
    }
  };

  const handleNavigate = (n) => {
    if (!n.isRead) handleMarkRead(n.notificationId);
    if (n.actionUrl) navigate(n.actionUrl);
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'warning': return <span className="notif-badge-icon warning">⚠️</span>;
      case 'success': return <span className="notif-badge-icon success">✅</span>;
      case 'action_required': return <span className="notif-badge-icon error">🔴</span>;
      default: return <span className="notif-badge-icon info">ℹ️</span>;
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  return (
    <div className="notifications-page-container">
      <nav className="breadcrumb">
        <Link to="/home" className="breadcrumb-item"><HiOutlineHome /> Home</Link>
        <span className="breadcrumb-sep">/</span>
        <span className="breadcrumb-current">Notifications Hub</span>
      </nav>

      <header className="page-header">
        <div className="header-text">
          <h1>Notification Center</h1>
          <p className="subtitle">Stay updated with critical alerts, pending actions, and system status.</p>
        </div>
        <button className="btn-mark-all" onClick={handleMarkAllRead}>
          <HiOutlineCheckCircle /> Mark All Read
        </button>
      </header>

      <div className="notif-tabs">
        {TAB_OPTIONS.map(tab => {
          const Icon = tab.icon;
          const active = activeTab === tab.value;
          return (
            <button 
              key={tab.value}
              className={`notif-tab-item ${active ? "active" : ""}`}
              onClick={() => setActiveTab(tab.value)}
            >
              <Icon />
              {tab.label}
            </button>
          );
        })}
      </div>

      <div className="notif-list-section">
        {loading ? (
          <div className="notif-loading-state">Refreshing alerts...</div>
        ) : notifications.length === 0 ? (
          <div className="notif-empty-state">
            <HiOutlineBellAlert className="empty-icon" />
            <h2>System all clear</h2>
            <p>You're all caught up! No recent alerts in this category.</p>
          </div>
        ) : (
          <>
            <div className="notif-items-wrapper">
              {notifications.map((n) => (
                <div 
                  className={`notif-large-item ${n.isRead ? "" : "unread"}`} 
                  key={n.notificationId}
                  onClick={() => handleNavigate(n)}
                >
                  <div className="notif-large-icon">
                    {getTypeIcon(n.type)}
                  </div>
                  <div className="notif-large-content">
                    <div className="notif-top">
                      <h3>{n.title}</h3>
                      <time>{formatDate(n.createdAt)}</time>
                    </div>
                    <p className="notif-body">{n.body}</p>
                    <div className="notif-bottom">
                      {n.moduleName && <span className="module-tag">{n.moduleName}</span>}
                      {!n.isRead && <span className="unread-pulse">New Action Item</span>}
                    </div>
                  </div>
                  <button 
                    className="notif-delete-btn" 
                    onClick={(e) => handleDelete(e, n.notificationId)}
                    title="Remove"
                  >
                    <HiOutlineTrash />
                  </button>
                </div>
              ))}
            </div>

            {hasMore && (
              <div className="pagination-wrap">
                <button 
                  className="btn-load-more" 
                  onClick={handleLoadMore} 
                  disabled={loadingMore}
                >
                  {loadingMore ? "Fetching older alerts..." : "Load Older Notifications"}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;
